import { useState } from 'react';
import { TrendingUp, TrendingDown, Pencil, Trash2, MoreVertical, ShoppingBag, Coins } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/format.js';
import { useDeletePettyCashMovement } from '../hooks/usePettyCash.js';

const KIND_CONFIG = {
  in: {
    label: 'Entrée',
    icon: TrendingUp,
    badge: 'bg-green-100 text-green-700',
    amount: 'text-green-700',
    sign: '+',
  },
  out: {
    label: 'Sortie',
    icon: TrendingDown,
    badge: 'bg-red-100 text-red-700',
    amount: 'text-red-700',
    sign: '-',
  },
  opening: {
    label: 'Ouverture',
    icon: Coins,
    badge: 'bg-slate-100 text-slate-600',
    amount: 'text-slate-700',
    sign: '',
  },
};

/**
 * Card d'un mouvement de caisse.
 * Mobile : layout card empilé. Desktop : row de table.
 *
 * Props :
 *   movement  — objet mouvement
 *   onEdit    — callback(movement)
 */
export default function PettyCashMovementCard({ movement, onEdit }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const deleteMut = useDeletePettyCashMovement();

  const config = KIND_CONFIG[movement.kind] ?? KIND_CONFIG.out;
  const KindIcon = config.icon;

  async function handleDelete() {
    if (!window.confirm('Supprimer ce mouvement ?')) return;
    setMenuOpen(false);
    await deleteMut.mutateAsync(movement.id);
  }

  function handleEdit() {
    setMenuOpen(false);
    onEdit?.(movement);
  }

  const commentDisplay =
    movement.comment && movement.comment.length > 60
      ? movement.comment.slice(0, 60) + '…'
      : movement.comment;

  return (
    <>
      {/* ── Mobile card ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm md:hidden">
        {/* Icône sens */}
        <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.badge}`}>
          <KindIcon size={15} aria-hidden />
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>
                {config.label}
              </span>
              {movement.category_name && (
                <span className="text-xs text-slate-500 truncate max-w-[140px]">
                  {movement.category_name}
                </span>
              )}
            </div>
            <p className={`text-base font-bold tabular-nums shrink-0 ${config.amount}`}>
              {config.sign}{formatCurrency(movement.amount_cents)}
            </p>
          </div>

          <p className="mt-0.5 text-xs text-slate-400">
            {formatDate(movement.movement_date)}
          </p>

          {commentDisplay && (
            <p className="mt-1 text-xs text-slate-600 line-clamp-2">{commentDisplay}</p>
          )}

          {movement.shopify_order_name && (
            <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
              <ShoppingBag size={11} aria-hidden />
              <span>{movement.shopify_order_name}</span>
            </div>
          )}
        </div>

        {/* Menu kebab */}
        {movement.kind !== 'opening' && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center justify-center rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors min-h-[40px] min-w-[40px]"
              aria-label="Actions sur le mouvement"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-20 w-32 rounded-lg border border-slate-200 bg-white shadow-lg py-1">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 min-h-[40px]"
                >
                  <Pencil size={13} aria-hidden />
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMut.isPending}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 min-h-[40px]"
                >
                  <Trash2 size={13} aria-hidden />
                  Supprimer
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Desktop row (masqué sur mobile) ─────────────────────────────────── */}
      <tr className="hidden md:table-row border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
        <td className="py-2.5 pl-4 pr-2 w-24">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>
            <KindIcon size={12} aria-hidden />
            {config.label}
          </span>
        </td>
        <td className="py-2.5 px-2 text-sm text-slate-500 whitespace-nowrap">
          {formatDate(movement.movement_date)}
        </td>
        <td className="py-2.5 px-2 text-sm text-slate-700 max-w-[160px] truncate">
          {movement.category_name ?? (movement.kind === 'opening' ? '—' : <span className="text-slate-400">Non classé</span>)}
        </td>
        <td className="py-2.5 px-2 text-sm text-slate-600 max-w-[200px] truncate">
          {commentDisplay ?? <span className="text-slate-300">—</span>}
        </td>
        <td className="py-2.5 px-2">
          {movement.shopify_order_name ? (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <ShoppingBag size={11} aria-hidden />
              {movement.shopify_order_name}
            </span>
          ) : (
            <span className="text-slate-300 text-xs">—</span>
          )}
        </td>
        <td className={`py-2.5 px-2 pr-4 text-sm font-semibold tabular-nums text-right ${config.amount}`}>
          {config.sign}{formatCurrency(movement.amount_cents)}
        </td>
        <td className="py-2.5 pl-2 pr-4 text-right">
          {movement.kind !== 'opening' && (
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={handleEdit}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                aria-label="Modifier ce mouvement"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMut.isPending}
                className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                aria-label="Supprimer ce mouvement"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </td>
      </tr>
    </>
  );
}
